<template>
  <tab-layout :full-width="true">
    <form
      slot="tabContent"
      class="row mx-0 show active"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div class="col px-0 pr-lg-3">
        <div class="bg-white rounded mb-4 p-3">
          <page-heading>{{ config.title }}</page-heading>

          <section class="mb-4">
            <span class="d-block mb-3">1. Upload a sponsors' logo.</span>
            <div class="f-form">
              <div v-for="n in config.logoCount" :key="n" class="f-form__half f-form__half--xl">
                <f-file
                  :id="`sponsorLogo${n}`"
                  :label="n === 1 ? 'Sponsor Logo' : `Sponsor Logo ${n}`"
                  accept="image/jpeg,image/png,image/gif"
                  hint="Maximum recommended file size 2MB"
                  :max-size="2097152"
                  :display-name="localFiles[`sponsor_logo_${n}`] ? localFiles[`sponsor_logo_${n}`].name : ''"
                  :is-invalid="!!errors[`sponsor_logo_${n}`]"
                  :tooltip="errors[`sponsor_logo_${n}`] ? errors[`sponsor_logo_${n}`][0] : null"
                  @change="onFileChange(`sponsor_logo_${n}`, $event)"
                />
                <f-image-preview
                  :src="localPreviews[`sponsor_logo_${n}`] || previews[`sponsor_logo_${n}`] || null"
                  alt="Sponsor Logo"
                  :deletable="!localPreviews[`sponsor_logo_${n}`] && !!previews[`sponsor_logo_${n}`]"
                  :deleting="deletingKey === `sponsor_logo_${n}`"
                  @delete="deleteImage(`sponsor_logo_${n}`)"
                />
                <f-text
                  v-if="config.hasLogoLinks"
                  :id="`sponsorLogo${n}Link`"
                  :label="`Sponsor Logo ${n} Link`"
                  :value="form[`sponsor_logo_${n}_link`]"
                  :error="errors[`sponsor_logo_${n}_link`] ? errors[`sponsor_logo_${n}_link`][0] : null"
                  @input="form[`sponsor_logo_${n}_link`] = $event"
                />
              </div>
            </div>
          </section>

          <section class="mb-4">
            <span class="d-block mb-3">2. Change the colour scheme of the {{ config.title.replace(' Appearance', '') }} area to custom one.</span>
            <div class="f-form">
              <div class="f-form__half f-form__half--xl">
                <f-color
                  id="schemaColor"
                  label="Select accent color"
                  :value="form.schema_color"
                  :error="errors.schema_color ? errors.schema_color[0] : null"
                  @input="form.schema_color = $event"
                />
              </div>
            </div>
          </section>

          <section class="mb-4">
            <span class="d-block mb-3">3. Upload advertisement banner.</span>
            <div class="f-form">
              <div class="f-form__half f-form__half--xl">
                <f-file
                  id="sponsorLogoDesktop"
                  label="Sponsor Banner Desktop"
                  accept="image/jpeg,image/png,image/gif"
                  hint="Maximum recommended file size 2MB"
                  :max-size="2097152"
                  :display-name="localFiles.sponsor_logo_desktop ? localFiles.sponsor_logo_desktop.name : ''"
                  :is-invalid="!!errors.sponsor_logo_desktop"
                  :tooltip="errors.sponsor_logo_desktop ? errors.sponsor_logo_desktop[0] : null"
                  @change="onFileChange('sponsor_logo_desktop', $event)"
                />
                <f-image-preview
                  v-if="localPreviews.sponsor_logo_desktop || previews.sponsor_logo_desktop"
                  :src="localPreviews.sponsor_logo_desktop || previews.sponsor_logo_desktop"
                  alt="Banner Desktop"
                  :deletable="!localPreviews.sponsor_logo_desktop && !!previews.sponsor_logo_desktop"
                  :deleting="deletingKey === 'sponsor_logo_desktop'"
                  @delete="deleteImage('sponsor_logo_desktop')"
                />
              </div>

              <div class="f-form__half f-form__half--xl">
                <f-file
                  id="sponsorLogoMobile"
                  label="Sponsor Logo Mobile"
                  accept="image/jpeg,image/png,image/gif"
                  hint="Maximum recommended file size 2MB"
                  :max-size="2097152"
                  :display-name="localFiles.sponsor_logo_mobile ? localFiles.sponsor_logo_mobile.name : ''"
                  :is-invalid="!!errors.sponsor_logo_mobile"
                  :tooltip="errors.sponsor_logo_mobile ? errors.sponsor_logo_mobile[0] : null"
                  @change="onFileChange('sponsor_logo_mobile', $event)"
                />
                <f-image-preview
                  v-if="localPreviews.sponsor_logo_mobile || previews.sponsor_logo_mobile"
                  :src="localPreviews.sponsor_logo_mobile || previews.sponsor_logo_mobile"
                  alt="Banner Mobile"
                  :deletable="!localPreviews.sponsor_logo_mobile && !!previews.sponsor_logo_mobile"
                  :deleting="deletingKey === 'sponsor_logo_mobile'"
                  @delete="deleteImage('sponsor_logo_mobile')"
                />
              </div>

              <div class="f-form__half f-form__half--xl">
                <f-text
                  id="sponsorLink"
                  label="Sponsor Link"
                  :value="form.sponsor_link"
                  :error="errors.sponsor_link ? errors.sponsor_link[0] : null"
                  @input="form.sponsor_link = $event"
                />
              </div>
            </div>
          </section>
        </div>

        <mobile-submit-button :loading="submitting || loading">
          Save
        </mobile-submit-button>
      </div>

      <desktop-action-bar>
        <app-button type="submit" class="px-md-5" :loading="submitting || loading">
          Save
        </app-button>
        <app-button variant="outline-primary" size="sm" class="mt-2" :loading="resetting || loading" @click="handleReset">
          Reset
        </app-button>
      </desktop-action-bar>
    </form>
  </tab-layout>
</template>

<script>
import { defineComponent, reactive, computed, watch, ref } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import FFile from '@/components/FormFields/FFile'
import FText from '@/components/FormFields/FText'
import FImagePreview from '@/components/FormFields/FImagePreview'
import FColor from '@/components/FormFields/FColor'
import MobileSubmitButton from '@/components/MobileSubmitButton'
import AppButton from '@/components/AppButton'
import DesktopActionBar from '@/components/DesktopActionBar'
import appearancesApi from '@/services/api/appearances'
import useAppearance from '@/composables/appearances/useAppearance'
import useAppearanceForm from '@/composables/appearances/useAppearanceForm'

export default defineComponent({
  name: 'AppearanceEdit',
  components: {
    'tab-layout': TabLayout,
    'page-heading': PageHeading,
    FFile,
    FText,
    FImagePreview,
    FColor,
    MobileSubmitButton,
    AppButton,
    DesktopActionBar
  },
  props: {
    frame: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const frameRef = computed(() => props.frame)
    const { loading, form, previews, config } = useAppearance(frameRef)
    const { submitting, resetting, errors, save, reset } = useAppearanceForm(frameRef, form, previews)

    const allFileKeys = ['sponsor_logo_1', 'sponsor_logo_2', 'sponsor_logo_3', 'sponsor_logo_desktop', 'sponsor_logo_mobile']

    const emptyFileMap = () => {
      const map = {}
      allFileKeys.forEach((key) => { map[key] = null })
      return map
    }

    const localFiles = reactive(emptyFileMap())
    const localPreviews = reactive(emptyFileMap())

    function clearLocalFiles() {
      allFileKeys.forEach((key) => {
        if (localPreviews[key]) URL.revokeObjectURL(localPreviews[key])
        localPreviews[key] = null
        localFiles[key] = null
      })
    }

    watch(loading, (isLoading) => {
      if (!isLoading) return
      clearLocalFiles()
    })

    watch(frameRef, clearLocalFiles)

    function onFileChange(key, event) {
      const file = event.target.files[0]
      if (!file) return
      localFiles[key] = file
      if (localPreviews[key]) URL.revokeObjectURL(localPreviews[key])
      localPreviews[key] = URL.createObjectURL(file)
    }

    const deletingKey = ref(null)

    async function deleteImage(key) {
      deletingKey.value = key
      try {
        await appearancesApi.deleteField(frameRef.value, key)
        previews[key] = null
      } finally {
        deletingKey.value = null
      }
    }

    async function handleSubmit() {
      const ok = await save(localFiles)
      if (ok) clearLocalFiles()
    }

    async function handleReset() {
      await reset()
      clearLocalFiles()
    }

    return {
      loading,
      submitting,
      resetting,
      errors,
      form,
      previews,
      config,
      localFiles,
      localPreviews,
      deletingKey,
      onFileChange,
      deleteImage,
      handleSubmit,
      handleReset
    }
  }
})
</script>




// WEBPACK FOOTER //
// src/views/Appearances/AppearanceEdit.vue